import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { Box, Chip, Drawer, IconButton, Typography } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { usePost } from '../../../utils/apiHelper';
import { IExternalAccountSyncAudit, IExternalAccountSyncRecord } from '../../../models/ExternalAccountSync/IExternalAccountSync';
import { getMockAudit } from '../ExternalAccountSyncMockData';

interface AuditHistoryDrawerProps {
    open: boolean;
    stagingId: number | null;
    // When provided (demo mode), audit history is derived locally instead of calling the API.
    demoRecord?: IExternalAccountSyncRecord | null;
    onClose: () => void;
}

const actionColor: Record<string, string> = {
    Approved: '#2e7d32',
    Rejected: '#b71c1c',
    Pulled: '#0d47a1',
};

const AuditHistoryDrawer: React.FC<AuditHistoryDrawerProps> = ({ open, stagingId, demoRecord, onClose }) => {
    const [audit, setAudit] = useState<IExternalAccountSyncAudit[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !stagingId) {
            setAudit([]);
            return;
        }

        if (demoRecord) {
            setAudit(getMockAudit(demoRecord));
            return;
        }

        setLoading(true);
        usePost<IExternalAccountSyncAudit[]>('ExternalAccountSyncAuditGet', { stagingId })
            .then((response) => setAudit(response.data || []))
            .finally(() => setLoading(false));
    }, [open, stagingId, demoRecord]);

    return (
        <Drawer open={open} anchor="right" onClose={onClose}>
            <Box display="flex" flexDirection="column" height="100%" width={480} maxWidth="95vw" sx={{ fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#007FFF', color: '#fff', height: 48, flexShrink: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Audit History</Typography>
                    <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 3, top: 3, color: 'white' }}>
                        <HighlightOffIcon style={{ fontSize: 28 }} />
                    </IconButton>
                </Box>

                <Box flexGrow={1} sx={{ overflowY: 'auto', padding: 2 }}>
                    {loading && <Typography variant="body2">Loading…</Typography>}
                    {!loading && audit.length === 0 && (
                        <Typography variant="body2" color="textSecondary">No audit history yet.</Typography>
                    )}
                    {!loading && audit.map((entry) => (
                        <Box key={entry.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <Chip
                                    size="small"
                                    label={entry.action}
                                    sx={{ backgroundColor: `${actionColor[entry.action] || '#616161'}1a`, color: actionColor[entry.action] || '#616161', fontWeight: 600 }}
                                />
                                <Typography variant="caption" color="textSecondary">
                                    {moment(entry.actionDate).format('MM/DD/YYYY HH:mm:ss')}
                                </Typography>
                            </Box>
                            {entry.actionByEmail && (
                                <Typography variant="body2">{entry.actionByEmail}</Typography>
                            )}
                            {entry.pulledBySystemCode && (
                                <Typography variant="body2">System: {entry.pulledBySystemCode}</Typography>
                            )}
                            {entry.rejectReason && (
                                <Typography variant="body2" color="textSecondary">
                                    Reason: {entry.rejectReason}{entry.rejectComments ? ` — ${entry.rejectComments}` : ''}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Drawer>
    );
};

export default AuditHistoryDrawer;
